import React, { useState } from 'react';
import { useBranches, useShifts, shiftsAPI } from '@/api';
import { Card, CardContent, Button, Input, Badge, Modal } from '@/components/ui';
import { useAuth } from '@/context/AuthContext';
import { useBranchScope } from '@/context/BranchScopeContext';
import { BranchFilterSelect, LoadingSpinner, Pagination } from '@/components/common';
import { Clock, Plus, Edit2, Trash2 } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { extractPaginatedData } from '@/utils/pagination';

export default function ShiftsPage() {
  const perPage = 6;
  const { can, user } = useAuth();
  const canManage = can('manage_shifts');
  const [page, setPage] = useState(1);
  const [showForm, setShowForm] = useState(false);
  const [editingShift, setEditingShift] = useState<any>(null);
  const { branchParam } = useBranchScope();
  // No fallback to user.branch_id here: "all branches" must actually mean all branches
  // the user may see, and the backend already enforces the real limit.
  const [branchFilter, setBranchFilter] = useState(branchParam);
  const { data: shiftsData, isLoading } = useShifts({ page, branch_id: branchFilter });
  const { data: branchesData } = useBranches();
  const queryClient = useQueryClient();

  const createMutation = useMutation({
    mutationFn: (data: any) => shiftsAPI.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shifts'] });
      setShowForm(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => shiftsAPI.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shifts'] });
      setEditingShift(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => shiftsAPI.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shifts'] });
    },
  });

  const { items: shifts, meta } = extractPaginatedData<any>(shiftsData, page, perPage);
  const branches = Array.isArray(branchesData?.data)
    ? branchesData.data
    : branchesData?.data?.data || [];

  const handleDelete = async (id: number) => {
    if (window.confirm('Вы уверены, что хотите удалить эту смену?')) {
      await deleteMutation.mutateAsync(id);
    }
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6 page-shell">
      <div className="page-toolbar">
        <h1 className="text-3xl font-bold">Смены</h1>
        {canManage && (
          <Button onClick={() => setShowForm(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Добавить смену
          </Button>
        )}
      </div>

      <Modal
        isOpen={showForm || !!editingShift}
        onClose={() => {
          setShowForm(false);
          setEditingShift(null);
        }}
        title={editingShift ? 'Редактировать смену' : 'Новая смена'}
      >
        <ShiftForm
          initialData={editingShift}
          branches={branches}
          userBranchId={user?.branch_id}
          onSubmit={async (data) => {
            if (editingShift) {
              await updateMutation.mutateAsync({ id: editingShift.id, data });
            } else {
              await createMutation.mutateAsync(data);
            }
          }}
          onCancel={() => {
            setShowForm(false);
            setEditingShift(null);
          }}
        />
      </Modal>

      <Card>
        <CardContent className="py-4">
          <div className="flex flex-col gap-4 sm:flex-row">
            <BranchFilterSelect
              alwaysShow
              value={branchFilter}
              onChange={(value) => {
                setPage(1);
                setBranchFilter(value);
              }}
            />
          </div>
        </CardContent>
      </Card>

      {/* Shifts Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {shifts.map((shift: any) => (
          <Card key={shift.id}>
            <CardContent className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <Clock className="w-8 h-8 text-blue-600" />
                  <div>
                    <h3 className="font-semibold">{shift.name}</h3>
                    <p className="text-sm text-gray-500">{shift.code}</p>
                  </div>
                </div>
                <Badge variant={shift.is_active ? 'success' : 'secondary'}>
                  {shift.is_active ? 'Активен' : 'Неактивен'}
                </Badge>
              </div>

              <div className="space-y-2 text-sm mb-4">
                <div className="flex justify-between">
                  <span className="text-gray-500">Филиал:</span>
                  <span>{shift.branch?.name || '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Начало:</span>
                  <span className="font-medium">{shift.start_time}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Конец:</span>
                  <span className="font-medium">{shift.end_time}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Tanaffus:</span>
                  <span>{shift.grace_minutes || 0} мин</span>
                </div>
              </div>

              {canManage && (
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setEditingShift(shift)}
                    className="flex-1"
                  >
                    <Edit2 className="w-4 h-4 mr-1" />
                    Редактировать
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDelete(shift.id)}
                  >
                    <Trash2 className="w-4 h-4 text-red-600" />
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
      <Pagination meta={meta} onPageChange={setPage} />

      {shifts.length === 0 && (
        <div className="text-center py-12">
          <Clock className="w-16 h-16 mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900">Смены не найдены</h3>
          <p className="text-gray-500">Создайте смену для начала работы.</p>
        </div>
      )}
    </div>
  );
}

function ShiftForm({
  initialData,
  branches,
  userBranchId,
  onSubmit,
  onCancel,
}: {
  initialData?: any;
  branches: any[];
  userBranchId?: number;
  onSubmit: (data: any) => void;
  onCancel: () => void;
}) {
  const [formData, setFormData] = useState({
    branch_id: initialData?.branch_id?.toString() || userBranchId?.toString() || '',
    name: initialData?.name || '',
    code: initialData?.code || '',
    start_time: (initialData?.start_time || '09:00').slice(0, 5),
    end_time: (initialData?.end_time || '18:00').slice(0, 5),
    grace_minutes: initialData?.grace_minutes ?? 15,
    is_active: initialData?.is_active ?? true,
  });

  const resolvedBranchId = formData.branch_id
    || (userBranchId ? String(userBranchId) : '')
    || (branches.length === 1 ? String(branches[0].id) : '');

  const handleSubmit = () => {
    onSubmit({
      ...formData,
      branch_id: Number(resolvedBranchId),
      grace_minutes: Number(formData.grace_minutes || 0),
      start_time: formData.start_time.slice(0, 5),
      end_time: formData.end_time.slice(0, 5),
    });
  };

  const isSubmitDisabled = !resolvedBranchId || !formData.name.trim() || !formData.code.trim();

  return (
    <div className="space-y-4">
      <select
        className="w-full px-4 py-2 border rounded-lg"
        value={resolvedBranchId}
        onChange={(e) => setFormData({ ...formData, branch_id: e.target.value })}
      >
        <option value="">Выберите филиал</option>
        {branches.map((branch: any) => (
          <option key={branch.id} value={branch.id}>
            {branch.name}
          </option>
        ))}
      </select>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Input
          placeholder="Название смены"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
        />
        <Input
          placeholder="Код (например, ДЕНЬ, НОЧЬ)"
          value={formData.code}
          onChange={(e) => setFormData({ ...formData, code: e.target.value })}
        />
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label className="text-sm text-gray-500 block mb-1">Время начала</label>
          <Input
            type="time"
            value={formData.start_time}
            onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
          />
        </div>
        <div>
          <label className="text-sm text-gray-500 block mb-1">Время окончания</label>
          <Input
            type="time"
            value={formData.end_time}
            onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
          />
        </div>
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label className="text-sm text-gray-500 block mb-1">Tanaffus / Break (minut)</label>
          <Input
            type="number"
            value={formData.grace_minutes}
            onChange={(e) => setFormData({ ...formData, grace_minutes: Number(e.target.value) })}
          />
          <p className="mt-1 text-xs text-gray-400">Tushlik yoki dam olish vaqti (masalan: 60 minut)</p>
        </div>
      </div>
      <div className="flex flex-col gap-2 sm:flex-row">
        <Button onClick={handleSubmit} className="flex-1" disabled={isSubmitDisabled}>
          Сохранить
        </Button>
        <Button onClick={onCancel} variant="outline" className="flex-1">
          Отмена
        </Button>
      </div>
    </div>
  );
}


